import { FileImportOptions } from "./FileImportHooks";
import { Localization, ObjectRef, PersistenceObjectType, PersistenceScope, SessionContext, SettingsManager, ViamapPersistenceLayer } from "@viamap/viamap2-common";
import { useState, useContext, useEffect, Fragment, useRef } from "react";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { Utils } from "@viamap/viamap2-common";
import { LayerHierarchy, LayerInfo, MapInfo, ViewerState } from "src/common/managers/Types";
import { ExifParserFactory } from "ts-exif-parser";
import { Persistence } from "../managers/Persistence";
import { MapitStateActionType, MapitStateContext, actionAddDataLayer, actionAddExternalFeatureLayers, actionSelectBackgroundLayer, actionSetLayerHierarchy, actionUpdateMapInfo, changeMapStyle } from "src/states/MapitState";
import { OldHierarchy } from "src/managers/OldHierarchy";
import { LayerFunc } from "src/managers/LayerFunc";
import { restoreMapFromLink } from "src/managers/RestoreMapLink";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { Feature } from "src/states/ApplicationStateFeatures";
import { AppMode } from "src/states/ApplicationStateFeatureMapping";
import { message } from "./Message";
import { FileLoaderXLSX } from "./FileLoaderXLSX";
import { WFStoGeoJson } from "src/functions/WFSutils";
import { attemptGetGPTSPDF, attemptGetGPTSRaw } from "./PDFUtils";
import { PDFDocument } from "pdf-lib";
import { DC } from "src/dataCollector/DataCollector";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { FormatUtils } from "src/managers/FormatUtils";

export function FileLoader(props: { fileImportOptions: FileImportOptions | null, finishCallback: () => void }) {
  let files = Array.from(props.fileImportOptions?.files || []);
  let [largeFileAccepted, setLargeFileAccepted] = useState<boolean>(false);
  let finished = useRef<number[]>([]);

  function handleSingleCallback(key: number) {
    if (!finished.current.includes(key))
      finished.current.push(key);
    if (finished.current.length === files.length) {
      props.finishCallback();
    }
  }

  if (props.fileImportOptions?.url) {
    return <UrlLoader url={props.fileImportOptions.url} fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />;
  }

  if (files.length === 0) {
    return (<></>);
  }

  if (
    !largeFileAccepted &&
    SettingsManager.getSystemSetting("enableFileSizeWarning", true) &&
    SettingsManager.getSystemSetting("importFilesizeWarningSize", 5500000) < files.reduce((a, b) => a + b.size, 0)
  ) {
    return (
      <ConfirmationDialog
        showWindow={1}
        header={"Large File Import Warning"}
        onSubmit={() => setLargeFileAccepted(true)}
        onCancel={props.finishCallback}
      >
        {Localization.getFormattedText("You are currently importing {size} of data", { size: FormatUtils.formatFileSize(files.reduce((a, b) => a + b.size, 0)) })}
      </ConfirmationDialog>
    )
  }

  return (
    <>
      {files.map((file, index) => (
        <Fragment key={file.name + "_" + index}>
          <AnyFileLoader file={file} fileImportOptions={props.fileImportOptions!} finishCallback={() => handleSingleCallback(index)} />
        </Fragment>
      ))}
    </>
  );
}

function AnyFileLoader(props: { file: File, fileImportOptions: FileImportOptions, finishCallback: () => void }) {
  useEffect(() => {
    console.log("Importing file: " + props.file.name);
    ActionTracker.track("FileImport", "File", "Load", { fileName: props.file.name, fileType: props.file.name.split('.').pop(), fileSize: props.file.size });

  }, []);

  switch (props.file.name.split('.').pop()!.toLowerCase()) {
    case "xls":
    case "xlsx":
      return <FileLoaderXLSX fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />
    case "jpg":
    case "jpeg":
    case "tiff":
      return <PictureFile file={props.file} fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />
    case "mapit":
    case "estateexplorer":
      return <MapitFile file={props.file} fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />
    case "geojson":
    case "json":
      return <JsonFile file={props.file} fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />
    case "gml":
      return <GMLFile file={props.file} fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />
    case "pdf":
      return <PDFFile file={props.file} fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />
    default:
      return <UnsupportedFile file={props.file} fileImportOptions={props.fileImportOptions} finishCallback={props.finishCallback} />
  }
}

function UrlLoader(props: { url: string, fileImportOptions: FileImportOptions, finishCallback: () => void }) {
  const { state: applicationState, hasAccessToFeature } = useContext(ApplicationStateContext);
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);

  useEffect(() => {

    function callbackToCreateHierarchy(layerHierarchy: LayerHierarchy, layerRenumberingMap: any): void {
      // Remove layers who have been assigned new ids.
      let oldGroupIdList = OldHierarchy.getAllGroupIds(mapitState.layerHierarchy);
      let newGroupIdList = OldHierarchy.getAllGroupIds(layerHierarchy);

      let lowestNumber = oldGroupIdList.length ? Math.min(...oldGroupIdList) : 0;

      let groupRenumberingMap = {};

      newGroupIdList.forEach((currentId, index) => { // Create group remapping object.
        groupRenumberingMap[currentId] = lowestNumber - index - 1;
      });

      // Assign new ids to both groups and layers.
      layerHierarchy = OldHierarchy.mapNumbersToIds(layerHierarchy, layerRenumberingMap, groupRenumberingMap);
      layerHierarchy = OldHierarchy.combineHierarchies(layerHierarchy, mapitState.layerHierarchy, layerRenumberingMap);

      mapitStateDispatch(actionSetLayerHierarchy(layerHierarchy));
    }

    let openLinkRequirements = (mapinfo: MapInfo) => {
      if (applicationState.appMode == AppMode.Normal && (mapinfo.allowLogin || hasAccessToFeature(Feature.DebuggingOpenAllLinks))
        || applicationState.appMode != AppMode.Normal
      ) {
        return true
      }
      return false
    }

    try {
      restoreMapFromLink(
        { ShortLink: props.url },
        openLinkRequirements,
        {
          callbackToCreateHierarchy: (layerHierarchy, layerRenumberingMap) => callbackToCreateHierarchy(layerHierarchy, layerRenumberingMap),
          callbackOnMapStyling: (mapInfo) => {
            mapitStateDispatch(actionUpdateMapInfo(mapInfo))
            mapitStateDispatch(changeMapStyle(mapInfo.mapStyle));
          },
          callbackOnLayerChanges: (a, layerInfo) => {
            const index = Math.round(Math.random() * 10000000) + 1000;
            layerInfo.layerId = index
            mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: layerInfo, zoomToFocus: false } })
            return index
          },
          callbackOnViewerStateChanges: (viewerState: ViewerState) => {
            (mapitState.map.getMapPop() as maplibregl.Map).flyTo({
              duration: 1000,
              zoom: (viewerState.zoom || viewerState.zoomFactor) ?? 12,
              center: viewerState.center ? [viewerState.center.lng, viewerState.center.lat] : [0, 0],
              pitch: viewerState.pitch ?? 0,
              bearing: viewerState.bearing ?? 0,
            })
          },
          callbackonNewRestrictions: (newRestrictions) => {
            const map = mapitState.map.getMapPop() as maplibregl.Map
            if (newRestrictions.maxZoom) map.setMaxZoom(newRestrictions.maxZoom)
            if (newRestrictions.minZoom) map.setMinZoom(newRestrictions.minZoom)
            if (newRestrictions.bounds) map.setMaxBounds(newRestrictions.bounds)
            if (newRestrictions.maxPitch) map.setMaxPitch(newRestrictions.maxPitch)
            if (newRestrictions.allowRotation === false) {
              map.dragRotate.disable();
              map.touchZoomRotate.disableRotation();
            }
          },
          callbackOnActiveFeatureLayerSpecs: (a) => { mapitStateDispatch(actionAddExternalFeatureLayers(a)) },
          callbackOnError: message.error,
          callbackExtras: (json) => {
            mapitStateDispatch(actionSelectBackgroundLayer(json.selectedBackgroundLayerKey))
          }
        }
      )
    } catch (err: any) {
      message.error(err)
    }
  }, []);

  return (<></>);
}

function PictureFile(props: { file: File, fileImportOptions: FileImportOptions, finishCallback: () => void }) {
  const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  const { state: sessionState } = useContext(SessionContext);

  useEffect(() => {
    let currentFile = props.file;
    let extension = props.file.name.split('.').pop()!.toLowerCase();
    const reader = new FileReader();
    reader.onload = async () => {
      function addMinutes(date: Date, minutes: number) {
        return new Date(date.getTime() + minutes * 60000);
      }
      let buffer = reader.result as ArrayBuffer;
      const data = ExifParserFactory.create(buffer).parse();

      let timeStampInLocalTime = new Date(data.tags!.DateTimeOriginal! * 1000);
      let offset = timeStampInLocalTime.getTimezoneOffset();
      let ts = addMinutes(timeStampInLocalTime, offset).toLocaleString();
      ts = ts === "Invalid Date" ? Localization.getText("No recorded date") : ts;

      if (data.tags && data.tags.GPSLatitude && data.tags.GPSLongitude) {
        let AWSMetaData: { [key: string]: string } = {
          filename: encodeURIComponent(currentFile.name),
          lat: data.tags.GPSLatitude.toString(),
          lon: data.tags.GPSLongitude.toString(),
        };
        if (sessionState.userSession) {
          AWSMetaData.userId = sessionState.userRef;
          AWSMetaData.userName = encodeURIComponent(sessionState.userName);
          let userRef = sessionState.userRef;
          let customerRef = sessionState.customerRef;
          if (customerRef && sessionState.userSession.getAccessToken()) {
            let persistenceLayer = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket", ""));
            const fileName = currentFile.name;
            const displayName = fileName.split('.').slice(0, -1).join('.');
            const fileExtension = fileName.split('.').at(-1)!.toLowerCase();

            let objectRef: ObjectRef = persistenceLayer.generateUniqueObjectRef(fileExtension);

            // Upload should happen when generating Link not just to store local files
            persistenceLayer.uploadObject(PersistenceScope.User, PersistenceObjectType.Image, customerRef, userRef, currentFile, objectRef, AWSMetaData, true)
              .then(awsData => {
                let exifData: { lat: number, lon: number, timestamp: string, imageType: string, img: string } = {
                  lat: data.tags!.GPSLatitude!,
                  lon: data.tags!.GPSLongitude!,
                  timestamp: ts,
                  imageType: extension,
                  img: (awsData as any).Location
                };
                let layerInfo = LayerFunc.createLayerInfoForOnePhoto(displayName, fileName, exifData);
                let countLoaded = 1;
                mapitStateDispatch(actionAddDataLayer(layerInfo, true));
                message.info(Localization.getFormattedText("Successfully loaded {count} elements from {file}", { count: countLoaded, file: currentFile.name }));
              }).finally(() => {
                message.progress();
                props.finishCallback();
              });
          } else {
            props.finishCallback();
            message.error("Cannot upload image without valid user session.");
          }
        } else {
          props.finishCallback();
          message.error("Cannot upload image without valid user session.");
        }
      } else {
        props.finishCallback();
        message.error("No geolocation data found in image: " + currentFile.name);
      }
    }
    reader.onprogress = (event) => {
      message.progress("Loading picture file...", event.loaded / event.total! * 100);
    }
    reader.onerror = (ex) => {
      message.progress()
      message.error(Localization.getFormattedText("Could not read file {file}. Lines read {number}", { file: props.file.name, error: ex.loaded }))
      props.finishCallback()
    }
    reader.readAsArrayBuffer(props.file);
  }, []);

  return (<></>)
}

function JsonFile(props: { file: File, fileImportOptions: FileImportOptions, finishCallback: () => void }) {
  const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => {
      console.log("Loading JSON file...");
      let ls = JSON.parse(reader.result as string);
      let fileName = props.file.name;
      const displayName = fileName.split('.').slice(0, -1).join('.');
      let currentDate = new Date();
      let countLoaded = Persistence.createLayersFromGeoJSON(
        displayName,
        ls,
        currentDate,
        (layerInfo) => {
          const index = Math.round(Math.random() * 10000000) + 1000;
          layerInfo.layerId = index
          mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: layerInfo, zoomToFocus: true } })
          return index
        },
      );
      message.info(Localization.getFormattedText("Loaded {count} layers from {file}", { count: countLoaded, file: displayName }));
      message.progress()
      props.finishCallback();
    };
    reader.onprogress = (event) => {
      message.progress(Localization.getFormattedText("Loading {type} file...", { type: "JSON" }), event.loaded / event.total! * 100);
    };
    reader.onerror = () => {
      message.error(Localization.getFormattedText("Error reading {file} file.", { file: props.file.name }));
      message.progress()
      props.finishCallback();
    };
    reader.readAsText(props.file);
  }, []);

  return (<></>)
}

function GMLFile(props: { file: File, fileImportOptions: FileImportOptions, finishCallback: () => void }) {
  const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        message.info(Localization.getText("Early version of GML import - please report any issues."));
        let geoJsonLayers = await WFStoGeoJson(reader.result as string).filter(({ geoJson }) => geoJson.features && geoJson.features.length > 0);
        if (geoJsonLayers.length === 0) {
          message.error(Localization.getText("No features found in GML file."));
          return
        }
        geoJsonLayers.forEach(({ layerName, geoJson }) => {
          console.log("GML Layer:", layerName, geoJson);
          Persistence.createLayersFromGeoJSON(layerName ?? "GML", geoJson, new Date(),
            (li: LayerInfo) => { mapitStateDispatch(actionAddDataLayer(li, true)) }
          );
        });
      } catch (error) {
        message.error("Fejl ved parsing af GML: " + error);
      } finally {
        message.progress()
        props.finishCallback();
      }
    }
    reader.onprogress = (event) => {
      message.progress(Localization.getFormattedText("Loading {type} file...", { type: "GML" }), event.loaded / event.total! * 100);
    }
    reader.onerror = () => {
      message.error(Localization.getFormattedText("Error reading {file} file.", { file: props.file.name }));
      message.progress()
      props.finishCallback();
    }
    reader.readAsText(props.file);

  }, []);

  return (<></>)
}

function MapitFile(props: { file: File, fileImportOptions: FileImportOptions, finishCallback: () => void }) {
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);

  function callbackToCreateHierarchy(layerHierarchy: LayerHierarchy, layerRenumberingMap: any): void {
    // Remove layers who have been assigned new ids.
    let oldGroupIdList = OldHierarchy.getAllGroupIds(mapitState.layerHierarchy);
    let newGroupIdList = OldHierarchy.getAllGroupIds(layerHierarchy);

    let lowestNumber = oldGroupIdList.length ? Math.min(...oldGroupIdList) : 0;

    let groupRenumberingMap = {};

    newGroupIdList.forEach((currentId, index) => { // Create group remapping object.
      groupRenumberingMap[currentId] = lowestNumber - index - 1;
    });

    // Assign new ids to both groups and layers.
    layerHierarchy = OldHierarchy.mapNumbersToIds(layerHierarchy, layerRenumberingMap, groupRenumberingMap);
    layerHierarchy = OldHierarchy.combineHierarchies(layerHierarchy, mapitState.layerHierarchy, layerRenumberingMap);

    mapitStateDispatch(actionSetLayerHierarchy(layerHierarchy));
  }

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        let ls = Utils.parseJSONPreserveDates(reader.result as string);
        let currentDate = new Date();
        mapitStateDispatch(actionSelectBackgroundLayer(ls.selectedBackgroundLayerKey))
        let countLoaded = await Persistence.createLayersFromJSON(
          ls,
          currentDate,
          (layerHierarchy, layerRenumberingMap) => callbackToCreateHierarchy(layerHierarchy, layerRenumberingMap),
          (mapInfo) => {
            mapitStateDispatch(actionUpdateMapInfo(mapInfo))
            mapitStateDispatch(changeMapStyle(mapInfo.mapStyle));
          },
          (layerInfo) => {
            const index = Math.round(Math.random() * 10000000) + 1000;
            layerInfo.layerId = index
            layerInfo.hasNoDataBehind = true
            mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: layerInfo, zoomToFocus: false } })
            return index
          },
          (viewerState: ViewerState) => {
            (mapitState.map.getMapPop() as maplibregl.Map).flyTo({
              duration: 1000,
              zoom: (viewerState.zoom || viewerState.zoomFactor) ?? 12,
              center: viewerState.center ? [viewerState.center.lng, viewerState.center.lat] : [0, 0],
              pitch: viewerState.pitch ?? 0,
              bearing: viewerState.bearing ?? 0,
            })
          },
        )
        message.info(Localization.getFormattedText("Successfully loaded {count} layers from {file}", { count: countLoaded, file: props.file.name }))
      } catch (error: any) {
        message.error(error?.message || error)
      } finally {
        props.finishCallback()
        message.progress()
      }
    };
    reader.onprogress = (event) => {
      message.progress("Loading Mapit file...", event.loaded / event.total! * 100);
    }
    reader.onerror = (ex) => {
      message.progress()
      message.error(Localization.getFormattedText("Could not read file {file}. Lines read {number}", { file: props.file.name, error: ex.loaded }))
      props.finishCallback()
    };
    reader.readAsText(props.file);
  }, []);


  return (<></>)
}

function PDFFile(props: { file: File, fileImportOptions: FileImportOptions, finishCallback: () => void }) {
  const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  useEffect(() => {

    const reader = new FileReader();
    reader.onload = async () => {
      try {

        message.progress("Processing PDF...", 0);
        PDFDocument.load(reader.result as ArrayBuffer).then(async (pdfDoc) => {
          let coords = await attemptGetGPTSPDF(pdfDoc);
          if (!coords) {
            message.error(Localization.getText("No georeferencing data (GPTS) found in PDF."));
            return;
          }
          DC.ViaDS.pdfToPng(reader.result as ArrayBuffer).then(async (response) => {
            if (!response.ok) {
              const errorText = await response.text();
              message.error(`Error: ${response.status} - ${errorText}`);
              return;
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const layerInfo = LayerFunc.createLayerInfoForImageOverlay(
              props.file.name.split(".").slice(0, -1).join("."),
              props.file.name.split(".").pop()!.toLowerCase(),
              url,
              coords,
            );
            mapitStateDispatch(actionAddDataLayer(layerInfo, true));
          }
          ).catch((err) => {
            message.error(`Fetch error: ${err.message}`);
          }).finally(() => {
            message.progress();
          });
        }).catch((err) => {
          message.error("Error processing PDF: " + err);
        });
      } catch (error) {
        message.error("Error reading PDF: " + error);
      } finally {
        message.progress()
        props.finishCallback();
      }
    }
    reader.readAsArrayBuffer(props.file);
  }, []);

  return (<></>)
}

function UnsupportedFile(props: {
  file: File;
  fileImportOptions: FileImportOptions;
  finishCallback: () => void;
}) {
  useEffect(() => {
    message.progress("Loading unknown file type...", 0);
    setTimeout(() => {
      message.progress();
      message.error(
        Localization.getFormattedText("Unsupported file type: {file}", { file: props.file.name }),
      );
      props.finishCallback();
    }, 500);
  }, []);

  return <></>;
}